import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch
} from '@mui/material';
import { 
  Close as CloseIcon,
  Send as SendIcon,
  Phone as PhoneIcon,
  Message as MessageIcon
} from '@mui/icons-material';

interface SmsModalProps {
  open: boolean;
  onClose: () => void;
  phoneNumber: string;
  defaultMessage: string;
  onSend: (phoneNumber: string, message: string) => Promise<boolean>;
  title?: string;
  templates?: Array<{ id: string; name: string; content: string; media?: Array<{ type: string; url: string }> }>;
  onSendMms?: (phoneNumber: string, message: string, fileIds: string[]) => Promise<boolean>;
  vehicleNumber?: string;
  cargo?: string;
  /** 이미 문자를 보낸 행(재전송)일 때만 표시 */
  showSentListButton?: boolean;
  onOpenSentList?: () => void;
}

const SmsModal: React.FC<SmsModalProps> = ({
  open,
  onClose,
  phoneNumber,
  defaultMessage,
  onSend,
  title,
  templates,
  onSendMms,
  vehicleNumber,
  cargo,
  showSentListButton = false,
  onOpenSentList
}) => {
  const [message, setMessage] = useState(defaultMessage);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [images, setImages] = useState<Array<{ url: string; fileId?: string }>>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState<number>(-1);
  const [combineMode, setCombineMode] = useState(true); // 템플릿 조합 모드

  // 모달이 열릴 때마다 메시지 초기화
  useEffect(() => {
    if (open) {
      // 공백/개행만 있는 초기값이면 비워서 placeholder가 보이도록 처리
      const initial = (defaultMessage || '').replace(/^\s+$/g, '');
      setMessage(initial.trim() === '' ? '' : initial);
      setError(null);
      setSuccess(false);
      setImages([]);
    }
  }, [open, defaultMessage]);

  // 전화번호 포맷팅 (하이픈 추가)
  const formatPhoneNumber = (phone: string) => {
    const clean = phone.replace(/[^0-9]/g, '');
    if (clean.length === 11) {
      return clean.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
    }
    if (clean.length === 10) {
      return clean.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    }
    return phone;
  };

  // 문자 발송 처리
  const handleSend = async () => {
    if (!message.trim()) {
      setError('메시지를 입력해주세요.');
      return;
    }
    if (images.length > 0 && !onSendMms) {
      setError('이미지가 첨부된 경우 MMS 발송만 가능합니다.');
      return;
    }

    setSending(true);
    setError(null);

    try {
      // MMS 우선 처리
      if (images.length > 0 && onSendMms) {
        // 업로드되지 않은 이미지 업로드
        const fileIds: string[] = [];
        for (let i = 0; i < images.length; i++) {
          const it = images[i];
          if (it.fileId) { fileIds.push(it.fileId); continue; }
          try {
            const res = await fetch('/api/sms/mms/upload', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ fileUrl: /^https?:\/\//i.test(it.url) ? it.url : undefined, fileBase64: /^data:image\//i.test(it.url) ? it.url : undefined })
            });
            const data = await res.json();
            if (!res.ok || !data?.fileId) throw new Error('업로드 실패');
            fileIds.push(data.fileId);
            images[i].fileId = data.fileId;
          } catch (e) {
            setSending(false);
            setError('이미지 업로드에 실패했습니다.');
            return;
          }
        }
        const success = await onSendMms(phoneNumber, message, fileIds);
        if (success) {
          setSuccess(true);
          setTimeout(() => onClose(), 1500);
        } else {
          setError('MMS 발송에 실패했습니다.');
        }
        return;
      }

      const success = await onSend(phoneNumber, message);
      if (success) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError('문자 발송에 실패했습니다. 다시 시도해주세요.');
      }
    } catch (error) {
      setError('문자 발송 중 오류가 발생했습니다.');
    } finally {
      setSending(false);
    }
  };

  // Enter 키로는 발송하지 않음 (버튼으로만 발송)

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 16,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
        }
      }}
    >
      {/* 휴대폰 헤더 */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        p: 2,
        borderRadius: '16px 16px 0 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhoneIcon sx={{ fontSize: 20 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title && title.trim() !== '' ? title : '문자 보내기'}
          </Typography>
        </Box>
        <IconButton 
          onClick={onClose} 
          sx={{ color: 'white' }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, background: '#f8f9fa' }}>
        {/* 수신자 정보 */}
        <Box sx={{ 
          p: 2, 
          background: 'white',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
              <Box sx={{ 
                width: 32, 
                height: 32, 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                flex: '0 0 auto'
              }}>
                <PhoneIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1 }}>
                  수신자
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#374151', lineHeight: 1.2 }}>
                  {(() => {
                    const detail = `${cargo ? String(cargo) : ''}${cargo && vehicleNumber ? ' · ' : ''}${vehicleNumber ? String(vehicleNumber) : ''}`;
                    const phone = formatPhoneNumber(phoneNumber);
                    return detail ? `${phone} / ${detail}` : phone;
                  })()}
                </Typography>
              </Box>
            </Box>
            {showSentListButton && onOpenSentList && (
              <Button
                variant="outlined"
                size="small"
                onClick={onOpenSentList}
                sx={{
                  flexShrink: 0,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#5c6bc0',
                  color: '#5c6bc0',
                  '&:hover': { borderColor: '#3949ab', bgcolor: 'rgba(92, 107, 192, 0.06)' }
                }}
              >
                보낸목록
              </Button>
            )}
          </Box>
        </Box>

        {/* 메시지 입력 영역 - 헤더 제거하여 공간 확보 */}
        <Box sx={{ p: 3, background: 'white' }}>
          
          <TextField
            multiline
            minRows={12}
            maxRows={24}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            
            placeholder="메시지를 입력하세요..."
            variant="outlined"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 12,
                fontSize: 16,
                '&:hover fieldset': {
                  borderColor: '#667eea',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                },
              },
              '& .MuiInputBase-input': {
                padding: '16px',
                lineHeight: 1.5,
              }
            }}
          />
          
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1, textAlign: 'right' }}>
            {message.length}/1000자
          </Typography>

          {/* 이미지 첨부(템플릿/URL) */}
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#555' }}>이미지 첨부</Typography>
              <Button size="small" variant="outlined" component="label">
                파일 선택
                <input type="file" accept="image/*" hidden onChange={async (e) => {
                  const file = e.target.files && e.target.files[0];
                  if (!file) return;
                  // 용량을 바로 업로드하지 않고 dataURL로 프리뷰 후 서버 업로드 시 base64로 전송
                  const reader = new FileReader();
                  reader.onload = () => {
                    const result = reader.result as string;
                    setImages(prev => [...prev, { url: result }]);
                  };
                  reader.readAsDataURL(file);
                  e.currentTarget.value = '';
                }} />
              </Button>
              <Button size="small" variant="text" color="inherit" onClick={() => setImages([])}>모두 제거</Button>
            </Box>
            {images.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {images.map((img, idx) => (
                  <Box
                    key={idx}
                    sx={{ position: 'relative', width: 64, height: 64, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden', background: '#fafafa', cursor: 'pointer' }}
                    onClick={() => { setPreviewIndex(idx); setPreviewOpen(true); }}
                  >
                    <img src={img.url} alt={`img-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button
                      aria-label="이미지 삭제"
                      title="삭제"
                      onClick={(e) => { e.stopPropagation(); setImages(prev => prev.filter((_, i) => i !== idx)); }}
                      style={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        background: '#ef4444',
                        color: '#ffffff',
                        border: '2px solid #ffffff',
                        borderRadius: '50%',
                        width: 24,
                        height: 24,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 16,
                        fontWeight: 700,
                        lineHeight: 1,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.35)'
                      }}
                    >
                      ×
                    </button>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* 템플릿 목록 (우측 배치 유사 효과를 위해 우측 정렬) */}
        {Array.isArray(templates) && templates.length > 0 && (
          <Box sx={{ p: 2, background: 'white', borderTop: '1px solid #eee' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#555' }}>템플릿</Typography>
              <FormControlLabel 
                control={<Switch size="small" checked={combineMode} onChange={(e) => setCombineMode(e.target.checked)} />}
                label="조합"
                sx={{ m: 0 }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {templates.map(t => (
                <Button 
                  key={t.id} 
                  variant="outlined" 
                  size="small" 
                  onClick={() => {
                    if (combineMode) {
                      setMessage(prev => {
                        const prefix = prev && prev.trim().length > 0 ? (prev.endsWith('\n') ? '' : '\n') : '';
                        return `${prev || ''}${prefix}${t.content}`;
                      });
                      if (t.media && t.media.length) {
                        const newItems = t.media.filter(m => m.url).map(m => ({ url: m.url }));
                        setImages(prev => {
                          const exists = new Set(prev.map(i => i.url));
                          const merged = [...prev];
                          newItems.forEach(it => { if (it.url && !exists.has(it.url)) merged.push(it); });
                          return merged;
                        });
                      }
                    } else {
                      setMessage(t.content);
                      if (t.media && t.media.length) {
                        setImages(t.media.filter(m => m.url).map(m => ({ url: m.url })));
                      } else {
                        setImages([]);
                      }
                    }
                  }} 
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  {t.name}
                </Button>
              ))}
            </Box>
          </Box>
        )}

        {/* 에러/성공 메시지 */}
        {error && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          </Box>
        )}
        
        {success && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              문자가 성공적으로 발송되었습니다!
            </Alert>
          </Box>
        )}
      </DialogContent>

      {/* 발송 버튼 */}
      <DialogActions sx={{ 
        p: 3, 
        background: 'white',
        borderRadius: '0 0 16px 16px',
        justifyContent: 'center'
      }}>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || !message.trim() || images.length > 0}
          startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: 25,
            px: 4,
            py: 1.5,
            fontSize: 16,
            fontWeight: 600,
            minWidth: 120,
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
            },
            '&:disabled': {
              background: '#ccc',
              transform: 'none',
              boxShadow: 'none',
            },
            transition: 'all 0.3s ease'
          }}
        >
          {sending ? '발송 중...' : '문자 발송'}
        </Button>
        {onSendMms && (
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={sending || !message.trim() || images.length === 0}
            sx={{
              ml: 2,
              background: images.length > 0 ? 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)' : '#ccc',
              color: 'white',
              borderRadius: 25,
              px: 4,
              py: 1.5,
              fontSize: 16,
              fontWeight: 600,
              minWidth: 120
            }}
          >
            MMS로 발송
          </Button>
        )}
      </DialogActions>
      {/* 이미지 미리보기 */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            background: '#000',
          }
        }}
      >
        <Box sx={{ position: 'relative', p: 0, bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconButton
            onClick={() => setPreviewOpen(false)}
            sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', zIndex: 1 }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
          {previewIndex >= 0 && images[previewIndex] && (
            <img
              src={images[previewIndex].url}
              alt={`preview-${previewIndex}`}
              style={{ maxWidth: '90vw', maxHeight: '80vh', width: 'auto', height: 'auto' }}
              onClick={() => setPreviewOpen(false)}
            />
          )}
        </Box>
      </Dialog>
    </Dialog>
  );
};

export default SmsModal;
